import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import PageRoutes, { APIRoutes } from "./constants";

/**
 * Single-flight refresh shared across every instance this is attached to, so a
 * burst of concurrent 401s (the schedule/favorites/channel calls all firing at
 * once) triggers exactly one /refreshToken request instead of a stampede.
 */
let refreshPromise: Promise<string | null> | null = null;

type StoredAuth = {
  AccessToken?: string;
  RefreshToken?: string;
  [key: string]: unknown;
};

function readAuth(): StoredAuth {
  try {
    const raw = localStorage.getItem("auth");
    return raw ? (JSON.parse(raw) as StoredAuth) : {};
  } catch {
    return {};
  }
}

function writeTokens(accessToken: string, refreshToken: string): void {
  const current = readAuth();
  localStorage.setItem(
    "auth",
    JSON.stringify({
      ...current,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
    })
  );
}

async function redirectToLogin(): Promise<void> {
  // v1 parity (src/hooks/useAxiosPrivate.ts Logout): also revoke the refresh
  // token server-side and clear the GA user id, not just the local auth blob.
  //
  // Logout is imported lazily (dynamic import) rather than statically: a
  // static top-level import of "@/hooks/useAxiosPrivate" here would pull in
  // useRefreshToken.ts, which imports the wrapped client from "../utils/axios"
  // - and that module imports attachAuthRefreshInterceptor from THIS file,
  // completing a real cycle back to this module. That cycle is only safe if
  // nothing dereferences the circular binding during module evaluation; but
  // src/utils/axios.ts calls axios.create(...) at its own module top level,
  // so merely importing this chain (regardless of whether Logout is ever
  // called) runs that top-level code eagerly. Confirmed by reproduction: a
  // static import broke every test in authRefreshInterceptor.test.ts, which
  // mocks the "axios" package with a stub that has no .create(), causing
  // "TypeError: default.create is not a function" the moment the module
  // graph loaded - before any test body ran. Loading the module lazily here
  // means that chain is only evaluated when a refresh actually fails, which
  // none of those tests trigger.
  try {
    const { Logout } = await import("@/hooks/useAxiosPrivate");
    Logout();
  } catch {
    // Best-effort: local cleanup below still runs regardless.
    localStorage.removeItem("auth");
  }
  // Hard redirect so all in-flight state resets, matching http-server's
  // behavior when a refresh ultimately fails.
  window.location.href = `${PageRoutes.Login}?user=true`;
}

async function performRefresh(apiUrl: string): Promise<string | null> {
  const auth = readAuth();
  if (!auth.RefreshToken || !auth.AccessToken) return null;
  try {
    // Bare axios (not an intercepted instance) so a 401 on the refresh call
    // itself cannot recurse back into this interceptor.
    const res = await axios.post(
      `${apiUrl}${APIRoutes.RefreshToken}`,
      { RefreshToken: auth.RefreshToken },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${auth.AccessToken}`,
        },
      }
    );
    const body = res?.data?.data;
    if (body?.AccessToken) {
      writeTokens(body.AccessToken, body.RefreshToken ?? auth.RefreshToken);
      return body.AccessToken as string;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Concurrent callers share one in-flight refresh.
 *
 * Exported because it has to be the only one. The server keeps a single
 * refresh token per user and replaces it on every refresh, so two requests
 * racing cannot both win: the first rotates the stored token and the second is
 * still holding the one before it, which comes back 400 and ends the session.
 * Anything in this app that needs a new access token comes through here.
 */
export function refreshAccessToken(apiUrl: string): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh(apiUrl).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Give an axios instance the same 401 -> refresh -> retry behavior that
 * http-server's axiosInstance already has, plus a request interceptor that
 * attaches the bearer token when a call site did not set one. The bare
 * instances in ./axios (default client and TeamClient) had no interceptors, so
 * v2 data hooks that call them directly kept failing on an expired token until
 * a manual page reload. Attaching this makes them refresh in place.
 */
export function attachAuthRefreshInterceptor(
  instance: AxiosInstance,
  apiUrl: string
): void {
  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    const headers = (config.headers ?? {}) as Record<string, string>;
    if (!headers["Authorization"]) {
      const token = readAuth().AccessToken;
      if (token) headers["Authorization"] = `bearer ${token}`;
    }
    config.headers = headers as AxiosRequestConfig["headers"];
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;
      // Never try to refresh on the refresh endpoint itself. This instance is
      // the shared default client, which v1's useRefreshToken also POSTs
      // /refreshToken through; without this guard a 401 there would fire a
      // second, redundant refresh and pre-empt v1's own failure handling.
      const isRefreshCall = String(original?.url ?? "").includes(APIRoutes.RefreshToken);
      if (status === 401 && original && !original._retry && !isRefreshCall) {
        original._retry = true;
        const newToken = await refreshAccessToken(apiUrl);
        if (newToken) {
          original.headers = original.headers ?? {};
          original.headers["Authorization"] = `bearer ${newToken}`;
          return instance(original);
        }
        await redirectToLogin();
      }
      return Promise.reject(error);
    }
  );
}

/** Exposed for tests: reset the shared single-flight state between cases. */
export function __resetRefreshStateForTests(): void {
  refreshPromise = null;
}
